
import { Component, Prop, Emit, Watch, Vue } from 'vue-property-decorator';
import DateTimePicker from '@/components/DateTimePicker.vue';
import Util from '@/utils/util';
import imageConvert from '@/utils/image';
import ProductPicker from '@/components/ProductPicker.vue';

@Component({
  components: { DateTimePicker, ProductPicker },
})
export default class CouponActivityForm extends Vue {
  @Prop({}) private activityType!: number;

  @Prop({}) private title!: string;

  @Prop({}) private couponProducts!: Array<{
    merchantId: number;
    productId: string;
    quantity: number;
  }>;

  @Prop({}) private wideImageUrl!: string;

  @Prop({}) private narrowImageUrl!: string;

  @Prop({}) private memo!: string;

  @Prop({}) private startTime!: string;

  @Prop({}) private endTime!: string;

  @Prop({}) private invitationCode!: string | null;

  @Prop({ default: false }) private loading!: boolean;

  @Prop({ default: false }) private isActivityStarted!: boolean;

  private valid: boolean = false;

  private activityTypes: Array<{ text: string; value: number }> = [
    { text: '活動', value: 1 },
    { text: '店家', value: 2 },
  ];

  private get showWidePicClear() {
    return this.form.wideImageUrl;
  }

  private get showNarrowPicClear() {
    return this.form.narrowImageUrl;
  }

  private form = {
    activityType: this.activityType,
    title: this.title,
    couponProducts: this.couponProducts,
    wideImageUrl: this.wideImageUrl,
    narrowImageUrl: this.narrowImageUrl,
    memo: this.memo,
    startTime: this.startTime,
    endTime: this.endTime,
    invitationCode: this.invitationCode,
  };

  private rules = {
    activityType: [this.requiredRule('類型')],
    title: [this.requiredRule('名稱'), this.maxLengthRule('名稱', 30)],
    invitationCode: [this.maxLengthRule('推薦代碼', 20)],
    memo: [this.maxLengthRule('注意事項', 2000)],
    startTime: [
      this.requiredRule('活動開始時間'),
      this.lessThenRule(
        '活動開始時間',
        '活動開始時間',
        (vm: CouponActivityForm) => vm.form.endTime,
      ),
    ],
    endTime: [
      this.requiredRule('活動結束時間'),
      this.greaterThenRule(
        '活動結束時間',
        '活動結束時間',
        (vm: CouponActivityForm) => vm.form.startTime,
      ),
    ],
    narrowPic: [
      this.imageRule(
        '寬圖',
        '窄圖',
        (vm) => vm.form.narrowImageUrl,
        (vm) => vm.form.wideImageUrl,
        (vm) => vm.$refs.wideFileInput,
      ),
    ],
    widePic: [
      this.imageRule(
        '寬圖',
        '窄圖',
        (vm) => vm.form.wideImageUrl,
        (vm) => vm.form.narrowImageUrl,
        (vm) => vm.$refs.narrowFileInput,
      ),
    ],
  };

  private get inputs() {
    return {
      activityType: this.activityType,
      title: this.title,
      couponProducts: this.couponProducts || [],
      wideImageUrl: this.wideImageUrl,
      narrowImageUrl: this.narrowImageUrl,
      memo: this.memo,
      startTime: this.startTime,
      endTime: this.endTime,
      invitationCode: this.invitationCode,
    };
  }

  private get datePeriod() {
    return {
      startOn: this.form.startTime,
      endOn: this.form.endTime,
    };
  }

  @Watch('inputs', { immediate: true })
  private inputsWatcher(val: any) {
    this.form = {
      ...val,
    };
  }

  @Watch('datePeriod')
  private datePeriodWatcher(val: any) {
    const inputs: any[] = (this.$refs.form as any).inputs.filter(
      (i: any) => i.label === '活動開始時間' || i.label === '活動結束時間',
    );
    inputs.forEach((input) => {
      input.validate(true);
    });
  }

  private requiredRule(column: string) {
    return (v: any) => !Util.isNullOrEmpty(v) || `${column} 為必填`;
  }

  private maxLengthRule(column: string, length: number) {
    const message = `${column} 最多 ${length} 字`;
    return (v: string) => !v || v.length <= length || message;
  }

  private lessThenRule(
    column: string,
    column2: string,
    func: (vm: CouponActivityForm) => any,
  ) {
    return (v: any) => {
      const c = func(this);
      return !v || !c || v <= c || `${column}要小於${column2}`;
    };
  }

  private greaterThenRule(
    column: string,
    column2: string,
    func: (vm: CouponActivityForm) => any,
  ) {
    return (v: any) => {
      const c = func(this);
      return !v || !c || v >= c || `${column}要大於${column2}`;
    };
  }

  private imageRule(
    column: string,
    column2: string,
    valueExp: (vm: CouponActivityForm) => any,
    compareExp: (vm: CouponActivityForm) => any,
    compareRefExp: (vm: CouponActivityForm) => any,
  ) {
    return (file: File) => {
      const v = valueExp(this);
      const c = compareExp(this);
      const ref = compareRefExp(this);
      if (file) {
        setTimeout(() => {
          ref.validate();
        }, 100);
      }
      return (
        !!(file || v) === !!c || `${column} 跟 ${column2} 要同時有值或沒值`
      );
    };
  }

  private handleClearImage(imageStyle: string) {
    if (imageStyle === 'wide') {
      this.form.wideImageUrl = '';
    } else {
      this.form.narrowImageUrl = '';
    }
    (this.$refs.narrowFileInput as any).validate();
    (this.$refs.wideFileInput as any).validate();
  }

  private async changeImage(event: any, imageStyle: string) {
    if (!event) return;
    const imagePic = await imageConvert(event);
    if (imageStyle === 'wide') {
      this.form.wideImageUrl = imagePic;
      (this.$refs.wideFileInput as any).clearableCallback();
    } else {
      this.form.narrowImageUrl = imagePic;
      (this.$refs.narrowFileInput as any).clearableCallback();
    }
  }

  private async cancel() {
    await this.$router.back();
  }

  @Emit()
  private async submit() {
    const form = this.$refs.form as any as { validate: () => boolean };
    if (form.validate()) {
      const couponProducts = this.form.couponProducts.map(
        (m: { productId: string; quantity: number }) => m,
      );
      return {
        ...this.form,
        couponProducts,
      };
    }
    return null;
  }
}
